import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Search, UserPlus, Home, UserX, ChevronLeft, ChevronRight, MoreVertical, Link, Crown } from 'lucide-react';

export default function NonTaggedPage() {
  const { api, isAdmin } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showTagDialog, setShowTagDialog] = useState(false);
  const [showMakeHofDialog, setShowMakeHofDialog] = useState(false);

  const limit = 25;

  const fetchMembers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit });
      if (search) params.append('search', search);
      
      const response = await api.get(`/members/non-tagged?${params}`);
      setMembers(response.data.members);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch non-tagged members');
    } finally {
      setLoading(false);
    }
  }, [api, page, search]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleTagMember = (member) => {
    setSelectedMember(member);
    setShowTagDialog(true);
  };

  const handleMakeHof = (member) => {
    setSelectedMember(member);
    setShowMakeHofDialog(true);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="non-tagged-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-slate-800">Non-Tagged Members</h1>
          <p className="text-sm text-slate-500">Members not linked to any family - Tag them to existing or new HOF</p>
        </div>
      </div>

      {/* Stats */}
      <Card className="border-0 shadow-card bg-orange-50">
        <CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <UserX className="w-8 h-8 text-orange-500" />
            <div>
              <p className="text-sm text-orange-600">Total Non-Tagged Members</p>
              <p className="text-2xl font-heading font-bold text-orange-700">{total}</p>
            </div>
          </div>
          <p className="text-xs text-orange-500">Only tagging action allowed</p>
        </CardContent>
      </Card>

      {/* Search */}
      <Card className="border-0 shadow-card">
        <CardContent className="p-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name, ITS, mobile..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-9"
              data-testid="non-tagged-search"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-0 shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Mobile</TableHead>
                <TableHead>ITS ID</TableHead>
                <TableHead>Age</TableHead>
                <TableHead>Gender</TableHead>
                <TableHead>Misaq Status</TableHead>
                {isAdmin && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="w-6 h-6 border-2 border-navy border-t-transparent rounded-full animate-spin mx-auto" />
                  </TableCell>
                </TableRow>
              ) : members.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    {search ? 'No members found matching your search' : 'No non-tagged members found'}
                  </TableCell>
                </TableRow>
              ) : (
                members.map((member) => (
                  <TableRow key={member.id} data-testid={`non-tagged-row-${member.id}`}>
                    <TableCell className="font-medium">{member.full_name}</TableCell>
                    <TableCell>{member.mobile || '-'}</TableCell>
                    <TableCell className="font-mono text-xs">{member.its_id || '-'}</TableCell>
                    <TableCell>{member.age || '-'}</TableCell>
                    <TableCell>{member.gender || '-'}</TableCell>
                    <TableCell>
                      <Badge className={member.misaq_status === 'Done' ? 'badge-success' : 'badge-warning'}>
                        {member.misaq_status || 'Not Done'}
                      </Badge>
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`non-tagged-actions-${member.id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleTagMember(member)}>
                              <Link className="w-4 h-4 mr-2" />
                              Tag to Existing HOF
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleMakeHof(member)}>
                              <Crown className="w-4 h-4 mr-2" />
                              Make HOF (New Family)
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t">
            <p className="text-sm text-slate-500">
              Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm">Page {page} of {totalPages}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Tag Dialog */}
      {showTagDialog && selectedMember && (
        <TagMemberDialog
          member={selectedMember}
          api={api}
          onClose={() => { setShowTagDialog(false); setSelectedMember(null); }}
          onSuccess={() => { fetchMembers(); setShowTagDialog(false); setSelectedMember(null); }}
        />
      )}

      {/* Make HOF Dialog */}
      {showMakeHofDialog && selectedMember && (
        <MakeHofDialog
          member={selectedMember}
          api={api}
          onClose={() => { setShowMakeHofDialog(false); setSelectedMember(null); }}
          onSuccess={() => { fetchMembers(); setShowMakeHofDialog(false); setSelectedMember(null); }}
        />
      )}
    </div>
  );
}

function TagMemberDialog({ member, api, onClose, onSuccess }) {
  const [action, setAction] = useState('');
  const [searchHof, setSearchHof] = useState('');
  const [hofResults, setHofResults] = useState([]);
  const [selectedHof, setSelectedHof] = useState(null);
  const [mohallas, setMohallas] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [newHofData, setNewHofData] = useState({
    mohalla: '',
    sector: '',
    address: '',
    building_tag: '',
    thaali_number: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchMohallas();
  }, []);

  useEffect(() => {
    if (newHofData.mohalla) {
      fetchSectors(newHofData.mohalla);
    } else {
      setSectors([]);
      setNewHofData(prev => ({ ...prev, sector: '' }));
    }
  }, [newHofData.mohalla]);

  const fetchMohallas = async () => {
    try {
      const response = await api.get('/mohallas');
      setMohallas(response.data.mohallas);
    } catch (error) {
      console.error('Failed to fetch mohallas');
    }
  };

  const fetchSectors = async (mohalla) => {
    try {
      const response = await api.get(`/sectors?mohalla=${encodeURIComponent(mohalla)}`);
      setSectors(response.data.sectors);
    } catch (error) {
      console.error('Failed to fetch sectors');
    }
  };

  const searchFamilies = async (query) => {
    if (!query || query.length < 2) {
      setHofResults([]);
      return;
    }
    try {
      const response = await api.get(`/families?search=${encodeURIComponent(query)}&limit=10`);
      setHofResults(response.data.families);
    } catch (error) {
      console.error('Failed to search families');
    }
  };

  const handleSubmit = async () => {
    if (!action) {
      toast.error('Please select an action');
      return;
    }

    if (action === 'tag_existing' && !selectedHof) {
      toast.error('Please select a family to tag the member');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        member_id: member.id,
        action: action,
        ...(action === 'tag_existing' ? { target_hof_id: selectedHof.id } : newHofData)
      };

      await api.post('/members/tag', payload);
      toast.success(action === 'tag_existing' 
        ? `Member tagged to ${selectedHof.hof_name}` 
        : 'New HOF created successfully');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to tag member');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Tag Member: {member.full_name}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Action Selection */}
          <div>
            <Label className="form-label">Select Action</Label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <Button
                type="button"
                variant={action === 'tag_existing' ? 'default' : 'outline'}
                className={action === 'tag_existing' ? 'bg-navy' : ''}
                onClick={() => setAction('tag_existing')}
              >
                <Home className="w-4 h-4 mr-2" />
                Tag to Existing HOF
              </Button>
              <Button
                type="button"
                variant={action === 'create_new_hof' ? 'default' : 'outline'}
                className={action === 'create_new_hof' ? 'bg-navy' : ''}
                onClick={() => setAction('create_new_hof')}
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Create New HOF
              </Button>
            </div>
          </div>

          {/* Tag to Existing HOF */}
          {action === 'tag_existing' && (
            <div className="space-y-3">
              <div>
                <Label className="form-label">Search Family</Label>
                <Input
                  placeholder="Search by HOF name, mobile, ITS..."
                  value={searchHof}
                  onChange={(e) => { setSearchHof(e.target.value); searchFamilies(e.target.value); }}
                />
              </div>
              
              {hofResults.length > 0 && (
                <div className="border rounded-lg max-h-48 overflow-y-auto">
                  {hofResults.map(hof => (
                    <div
                      key={hof.id}
                      className={`p-3 cursor-pointer hover:bg-slate-50 border-b last:border-0 ${selectedHof?.id === hof.id ? 'bg-blue-50' : ''}`}
                      onClick={() => setSelectedHof(hof)}
                    >
                      <p className="font-medium">{hof.hof_name}</p>
                      <p className="text-xs text-slate-500">
                        {hof.mobile} • {hof.mohalla || 'No Mohalla'} / {hof.sector || 'No Sector'}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {selectedHof && (
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm font-medium text-green-800">Selected: {selectedHof.hof_name}</p>
                  <p className="text-xs text-green-600">
                    {selectedHof.mohalla} / {selectedHof.sector} • Thaali: {selectedHof.thaali_number || 'N/A'}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Create New HOF */}
          {action === 'create_new_hof' && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="form-label">Mohalla</Label>
                  <Select 
                    value={newHofData.mohalla} 
                    onValueChange={(v) => setNewHofData(prev => ({ ...prev, mohalla: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Mohalla" />
                    </SelectTrigger>
                    <SelectContent>
                      {mohallas.map(m => (
                        <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="form-label">Sector</Label>
                  <Select 
                    value={newHofData.sector} 
                    onValueChange={(v) => setNewHofData(prev => ({ ...prev, sector: v }))}
                    disabled={!newHofData.mohalla}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={newHofData.mohalla ? "Select Sector" : "Select Mohalla first"} />
                    </SelectTrigger>
                    <SelectContent>
                      {sectors.map(s => (
                        <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="form-label">Address</Label>
                <Input
                  value={newHofData.address}
                  onChange={(e) => setNewHofData(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Enter address"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="form-label">Building Tag</Label>
                  <Input
                    value={newHofData.building_tag}
                    onChange={(e) => setNewHofData(prev => ({ ...prev, building_tag: e.target.value }))}
                    placeholder="e.g., Block A"
                  />
                </div>
                <div>
                  <Label className="form-label">Thaali Number (Optional)</Label>
                  <Input
                    value={newHofData.thaali_number}
                    onChange={(e) => setNewHofData(prev => ({ ...prev, thaali_number: e.target.value }))}
                    placeholder="e.g., 401"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button 
            onClick={handleSubmit} 
            disabled={loading || !action}
            className="bg-navy hover:bg-navy-light"
          >
            {loading ? 'Processing...' : 'Tag Member'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// MakeHofDialog - For creating a new family with this member as HOF
function MakeHofDialog({ member, api, onClose, onSuccess }) {
  const [mohallas, setMohallas] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [formData, setFormData] = useState({
    mohalla: '',
    sector: '',
    thaali_number: '',
    address: '',
    flat_number: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchMohallas();
  }, []);

  useEffect(() => {
    if (formData.mohalla) {
      fetchSectors(formData.mohalla);
    }
  }, [formData.mohalla]);

  const fetchMohallas = async () => {
    try {
      const response = await api.get('/mohallas');
      setMohallas(response.data.mohallas || []);
    } catch (error) {
      console.error('Failed to fetch mohallas');
    }
  };

  const fetchSectors = async (mohallaName) => {
    try {
      const response = await api.get(`/sectors?mohalla=${encodeURIComponent(mohallaName)}`);
      setSectors(response.data.sectors || []);
    } catch (error) {
      console.error('Failed to fetch sectors');
    }
  };

  const handleMakeHof = async () => {
    if (!formData.mohalla || !formData.sector) {
      toast.error('Please select Mohalla and Sector');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post(`/members/${member.id}/make-hof`, formData);
      const data = response.data;
      toast.success(
        <div>
          <p className="font-semibold">New family created!</p>
          <p className="text-sm">HOF: {data.hof_name}</p>
          <p className="text-sm">ITS: {data.hof_its}</p>
          <p className="text-sm">{data.mohalla} - {data.sector}</p>
        </div>,
        { duration: 5000 }
      );
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create family');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Crown className="w-5 h-5 text-amber-500" />
            Make HOF (Create New Family)
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
            <p className="text-sm text-emerald-800">
              New HOF: <span className="font-semibold">{member.full_name}</span>
            </p>
            <p className="text-xs text-emerald-600 mt-1">
              ITS: {member.its_id || 'N/A'}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="form-label">Mohalla *</Label>
              <Select 
                value={formData.mohalla} 
                onValueChange={(v) => setFormData({...formData, mohalla: v, sector: ''})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Mohalla" />
                </SelectTrigger>
                <SelectContent>
                  {mohallas.map(m => (
                    <SelectItem key={m.id || m.name} value={m.name}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="form-label">Sector *</Label>
              <Select 
                value={formData.sector} 
                onValueChange={(v) => setFormData({...formData, sector: v})}
                disabled={!formData.mohalla}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Sector" />
                </SelectTrigger>
                <SelectContent>
                  {sectors.map(s => (
                    <SelectItem key={s.id || s.name} value={s.name}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="form-label">Thaali Number (Optional)</Label>
            <Input
              value={formData.thaali_number}
              onChange={(e) => setFormData({...formData, thaali_number: e.target.value})}
              placeholder="e.g., 123"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="form-label">Address / Building</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                placeholder="Building name"
              />
            </div>
            <div>
              <Label className="form-label">Flat Number</Label>
              <Input
                value={formData.flat_number}
                onChange={(e) => setFormData({...formData, flat_number: e.target.value})}
                placeholder="e.g., A-101"
              />
            </div>
          </div>

          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            <p className="font-medium">What will happen:</p>
            <ul className="list-disc list-inside mt-1 text-xs">
              <li>A new family will be created with this member as HOF</li>
              <li>Member will be assigned to the selected Mohalla/Sector</li>
              <li>Member's relationship will change to "HOF"</li>
            </ul>
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button 
            onClick={handleMakeHof} 
            disabled={loading || !formData.mohalla || !formData.sector}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {loading ? 'Creating...' : 'Create New Family'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
